// A mask preset's shape (#266, spec docs/specs/editor/animations.md), as live's reveal masks show a
// clip: on its body, `width` by `height` about its centre, showing `q` of it (the preset's
// `initial` and the rest by its progress). Pure: the scene draws it (`motion.ts`), and a preset's
// tile clips its preview with it.
import type { AnimationOrigin } from "../../../api/generated/model";
import type { MaskAt } from "./animate";

export interface Point {
  x: number;
  y: number;
}

/** What shows: a polygon, a rounded box, or a circle; an empty polygon shows nothing. */
export type MaskShapeAt =
  | { kind: "polygon"; points: readonly Point[] }
  | { kind: "box"; x: number; y: number; width: number; height: number; radius: number }
  | { kind: "circle"; x: number; y: number; radius: number };

/** The corner (or the centre) an origin names, on a box `w` by `h` about its centre. */
function cornerOf(origin: AnimationOrigin, w: number, h: number): Point {
  switch (origin) {
    case "center":
      return { x: 0, y: 0 };
    case "top-left":
      return { x: -w / 2, y: -h / 2 };
    case "top-right":
      return { x: w / 2, y: -h / 2 };
    case "bottom-left":
      return { x: -w / 2, y: h / 2 };
    case "bottom-right":
      return { x: w / 2, y: h / 2 };
  }
}

const box = (x: number, y: number, width: number, height: number, radius = 0): MaskShapeAt =>
  width <= 0 || height <= 0
    ? { kind: "polygon", points: [] }
    : { kind: "box", x, y, width, height, radius: Math.min(radius, width / 2, height / 2) };

/**
 * The mask on a body `w` by `h` (the frame's pixels), `unit` frame pixels to one of live's, the
 * clip drawn at `scale` (Edge Crop keeps its cut the same on screen however it scales).
 */
export function maskShape(mask: MaskAt, w: number, h: number, unit: number, scale: number): MaskShapeAt {
  const { animation } = mask;
  const q = animation.initial + (1 - animation.initial) * mask.progress;
  const radius = animation.borderRadius * unit;
  switch (mask.shape) {
    case "wipe":
      // A band from the start edge, `q` of the way across.
      if (animation.direction === "left") return box(-w / 2, -h / 2, q * w, h);
      if (animation.direction === "right") return box(w / 2 - q * w, -h / 2, q * w, h);
      if (animation.direction === "top") return box(-w / 2, -h / 2, w, q * h);
      return box(-w / 2, h / 2 - q * h, w, q * h);
    case "center": {
      // Bands from the middle out: across the height, the width, or both at once, a cross (live's).
      if (animation.axis === "vertical") return box(-w / 2, (-q * h) / 2, w, q * h);
      if (animation.axis === "horizontal") return box((-q * w) / 2, -h / 2, q * w, h);
      if (q <= 0) return { kind: "polygon", points: [] };
      const a = (q * w) / 2;
      const b = (q * h) / 2;
      return {
        kind: "polygon",
        points: [
          { x: -a, y: -h / 2 },
          { x: a, y: -h / 2 },
          { x: a, y: -b },
          { x: w / 2, y: -b },
          { x: w / 2, y: b },
          { x: a, y: b },
          { x: a, y: h / 2 },
          { x: -a, y: h / 2 },
          { x: -a, y: b },
          { x: -w / 2, y: b },
          { x: -w / 2, y: -b },
          { x: -a, y: -b },
        ],
      };
    }
    case "angle": {
      // A band `q` of the way across the clip's diagonal square, turned by the angle.
      if (q <= 0) return { kind: "polygon", points: [] };
      const side = Math.hypot(w, h);
      const turn = (animation.angle * Math.PI) / 180;
      const turned = ([x, y]: readonly [number, number]): Point => ({
        x: x * Math.cos(turn) - y * Math.sin(turn),
        y: x * Math.sin(turn) + y * Math.cos(turn),
      });
      return {
        kind: "polygon",
        points: (
          [
            [-side / 2, -side / 2],
            [-side / 2 + q * side, -side / 2],
            [-side / 2 + q * side, side / 2],
            [-side / 2, side / 2],
          ] as const
        ).map(turned),
      };
    }
    case "circle": {
      // From the origin, out to the farthest corner.
      if (q <= 0) return { kind: "polygon", points: [] };
      const from = cornerOf(animation.origin, w, h);
      const reach = Math.max(
        ...(
          [
            [-w / 2, -h / 2],
            [w / 2, -h / 2],
            [-w / 2, h / 2],
            [w / 2, h / 2],
          ] as const
        ).map(([x, y]) => Math.hypot(x - from.x, y - from.y)),
      );
      return { kind: "circle", x: from.x, y: from.y, radius: q * reach };
    }
    case "rect": {
      // A box `q` of the clip's size, grown from the origin's corner (or the middle).
      const from = cornerOf(animation.origin, w, h);
      const rw = q * w;
      const rh = q * h;
      const x = animation.origin === "center" ? -rw / 2 : from.x < 0 ? -w / 2 : w / 2 - rw;
      const y = animation.origin === "center" ? -rh / 2 : from.y < 0 ? -h / 2 : h / 2 - rh;
      return box(x, y, rw, rh, radius);
    }
    case "edge": {
      // The clip's edges cut in by the margins as `q` falls; the origin's corner holds.
      const ix = (animation.xMargin * unit * (1 - q)) / scale;
      const iy = (animation.yMargin * unit * (1 - q)) / scale;
      const from = animation.origin;
      const left = from === "center" || from === "top-right" || from === "bottom-right" ? ix : 0;
      const right = from === "center" || from === "top-left" || from === "bottom-left" ? ix : 0;
      const top = from === "center" || from === "bottom-left" || from === "bottom-right" ? iy : 0;
      const bottom = from === "center" || from === "top-left" || from === "top-right" ? iy : 0;
      return box(-w / 2 + left, -h / 2 + top, w - left - right, h - top - bottom, radius);
    }
  }
}
